<script lang="ts">
  /**
   * Discovery — where a community ranks in Skool's discovery search, and what to target
   * next.
   *
   * It is also where signup carries on. A new account lands here the moment it exists,
   * while Sideguide's Skool account is still getting into the community and the import is
   * still running, so the page opens with a setup card that reports both, and offers the
   * Helper once there is something for it to keep current. The audit underneath started
   * when the link was pasted, and has usually finished by the time anyone looks.
   *
   * WHICH AUDIT. While a claim is in flight — a new account, or an owner adding a
   * community — it is the one that claim started (`/v1/onboarding`'s `audit_id`). Otherwise
   * it is the selected community's newest audit, and one is run when there is none.
   *
   * `?add` opens the claim form, for an owner adding another community.
   */
  import { navigate } from '../lib/router.svelte';
  import AdminShell from './AdminShell.svelte';
  import PageContainer from '../lib/ui/PageContainer.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';
  import CommunityLogo from '../lib/ui/CommunityLogo.svelte';
  import HelperInstallSteps from '../lib/ui/HelperInstallSteps.svelte';
  import RankingTable from '../lib/ui/audit/RankingTable.svelte';
  import LanguageSelect from '../lib/ui/audit/LanguageSelect.svelte';
  import KeywordImprove from '../lib/ui/audit/KeywordImprove.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import { AuditRun } from '../lib/audit/run.svelte';
  import { tailAudit } from '../lib/audit';
  import { getToken, handle401, type AdminUser } from '../lib/admin';
  import { asAdminUser, ensureBootstrap, refreshBootstrap } from '../lib/session';
  import { activeCommunity } from '../lib/community.svelte';
  import { helper, ensureHelper } from '../lib/helper.svelte';
  import {
    claimCommunity,
    confirmAccess,
    fetchLatestAudit,
    fetchOnboarding,
    isCodedError,
    runAudit,
    rerunSignupAudit,
    submitJoinAnswers,
    type Onboarding,
  } from '@api';

  // What each import step is called on screen, keyed by the flow's job names — the server
  // reports them in the order they run (`jobs.flows.ONBOARDING.steps`).
  //
  // TWO VOCABULARIES, ONE MAP, because the server has two sources for the same progress
  // bar (`jobs/signup_access.indexing`): the job ledger while the onboarding flow is what
  // ingests a community, and the walk registry once it holds standing walks for it. The
  // registry's steps are named for what an owner is watching arrive — `feed`, `roster`,
  // `classroom` — not for the job that used to fetch it, and a community mid-cut-over can
  // report either set. An unmapped key falls through to itself, so the missing three read
  // as raw `feed`/`roster`/`classroom` on the one screen a new owner ever sees.
  const STEP_LABELS: Record<string, string> = {
    'skool-community-meta': 'Community details',
    'skool-community-feed': 'Posts & comments',
    'skool-community-members': 'Members',
    'skool-community-crm': 'Pipelines',
    'skool-community-classrooms': 'Classroom',
    feed: 'Posts & comments',
    roster: 'Members',
    classroom: 'Classroom',
  };
  // Ledger statuses that mean a step is behind us. `partial` ran and kept what it could.
  const FINISHED = new Set(['ok', 'skipped', 'partial']);
  const NO_AUDIT = 'There is no report for this community yet.';
  // A setup card is watching an import measured in minutes; five seconds reads as live.
  const POLL_MS = 5000;
  // An owner who was asked to invite us may never press the button, so look on our own —
  // slowly, because each look re-reads our whole membership list from Skool.
  const INVITE_CHECK_MS = 60_000;

  let me = $state<AdminUser | null>(null);
  let onboarding = $state<Onboarding | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);

  const run = new AuditRun();
  let improving = $state(false);
  // Why no audit is on screen, when none is: no keywords on Skool, or a failed request.
  let auditNote = $state<string | null>(null);

  let adding = $state(new URLSearchParams(window.location.search).has('add'));
  let claimUrl = $state('');
  let claiming = $state(false);
  let claimError = $state<string | null>(null);
  let confirming = $state(false);
  let confirmError = $state<string | null>(null);
  let copied = $state(false);
  // The owner's answers to the community's join questions, drafted by us and theirs to
  // change. Kept beside `onboarding` rather than read out of it, because they are being
  // edited while the page keeps polling.
  let answers = $state<string[]>([]);
  let joining = $state(false);
  let joinError = $state<string | null>(null);

  const community = $derived(onboarding?.community ?? null);
  const joinForm = $derived(
    onboarding?.access === 'needs_answers' ? (onboarding.join_form ?? []) : null,
  );
  const answersReady = $derived(
    !!joinForm && answers.length === joinForm.length && answers.every((a) => a.trim().length > 0),
  );
  const claimInFlight = $derived(!!community && !onboarding?.community_held);
  const indexing = $derived(onboarding?.indexing ?? null);
  const importing = $derived(!!indexing && indexing.state !== 'done');
  const holdsCommunity = $derived((me?.skool_ids.length ?? 0) > 0);
  const isCreator = $derived(me?.role === 'creator');
  const showClaim = $derived(isCreator && (adding || onboarding?.stage === 'no_community'));
  // The Helper is the next thing to do once we are inside the community — before that it
  // would have nothing to keep current.
  const offerHelper = $derived(
    isCreator &&
      (holdsCommunity || onboarding?.access === 'member') &&
      helper.loaded &&
      helper.state !== 'ready',
  );
  const auditUrl = $derived(run.community ? `https://www.skool.com/${run.community.slug}` : '');
  // The report runs from the moment the link is pasted, but it is not what somebody
  // waiting to be let in is here for — so it appears once we are inside, where it is the
  // thing to read while the import runs. A held community always shows its own.
  // Skool's members page, where the Invite button is — two clicks saved, and no hunting
  // for a settings screen whose name changes.
  const membersUrl = $derived(community ? `${community.url}/-/members` : '');
  const auditVisible = $derived(
    !isCreator || activeCommunity() !== null || (claimInFlight && onboarding?.access === 'member'),
  );
  // A community that sets no keywords has nothing to rank, so the suggestions ARE the
  // report and the page opens on them.
  const rankless = $derived(run.plan.length === 0 && run.recs !== null);

  function messageOf(err: unknown): string {
    return err instanceof Error ? err.message : 'Something went wrong.';
  }

  // Seed the editable answers from the draft the moment the form arrives, and only then:
  // re-seeding on a later poll would overwrite what the owner is typing.
  $effect(() => {
    const form = joinForm;
    if (form && answers.length !== form.length) answers = form.map((q) => q.answer);
  });

  async function submitJoin(e: SubmitEvent): Promise<void> {
    e.preventDefault();
    if (joining || !answersReady) return;
    joining = true;
    joinError = null;
    try {
      onboarding = await submitJoinAnswers([...answers]);
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      joinError = messageOf(err);
    } finally {
      joining = false;
    }
  }

  async function init(): Promise<void> {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    try {
      const [boot, ob] = await Promise.all([ensureBootstrap(), fetchOnboarding()]);
      me = asAdminUser(boot);
      onboarding = ob;
      if (me.role === 'creator') void ensureHelper();
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      error = messageOf(err);
    } finally {
      loading = false;
    }
  }
  void init();

  // Which audit is on screen follows what the account is looking at: the claim's while one
  // is in flight, the selected community's otherwise. Re-derived on every poll and every
  // community switch; `openedFor` and `shown` keep an unchanged answer from reopening the
  // stream it already has.
  let openedFor: string | null = null;
  let shown: number | null = null;

  $effect(() => {
    if (!onboarding || !auditVisible) return;
    const selected = activeCommunity();
    const auditId = onboarding.audit_id;
    const key = claimInFlight ? `claim:${auditId}` : selected ? `community:${selected.id}` : null;
    if (key === null || key === openedFor) return;
    openedFor = key;
    if (claimInFlight) showAudit(auditId);
    else if (selected) void openCommunityAudit(selected.id);
  });

  function showAudit(id: number | null): void {
    auditNote = id === null ? NO_AUDIT : null;
    if (id === null || id === shown) return;
    shown = id;
    improving = false;
    void run.run((h) => tailAudit(id, h));
  }

  // null means the community's own indexed language. Changing it and re-running is
  // how an owner asks "and where do I rank in German?".
  let language = $state<string | null>(null);

  async function openCommunityAudit(skoolId: number): Promise<void> {
    try {
      showAudit((await fetchLatestAudit(skoolId)).id);
    } catch (err: unknown) {
      if (isCodedError(err) && err.code === 'audit_not_found') await startAudit(skoolId);
      else if (!handle401(err, navigate)) auditNote = messageOf(err);
    }
  }

  async function startAudit(skoolId: number): Promise<void> {
    try {
      showAudit((await runAudit(skoolId, language)).id);
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      auditNote = messageOf(err);
    }
  }

  function rerun(): void {
    const selected = activeCommunity();
    if (selected) void startAudit(selected.id);
  }

  // Changing the language IS a re-run — but which endpoint depends on whether the
  // community is held yet. During setup there is nothing to address by id, so the claim
  // asks the onboarding resource; the audit it starts replaces the one on screen.
  async function relanguage(): Promise<void> {
    if (activeCommunity()) {
      rerun();
      return;
    }
    try {
      const next = await rerunSignupAudit(language);
      if (next.audit_id) showAudit(next.audit_id);
    } catch (err: unknown) {
      if (!handle401(err, navigate)) auditNote = messageOf(err);
    }
  }

  // While setting up, re-read every few seconds. The moment the claim is granted the
  // bootstrap is refreshed, so the shell, the sidebar and the switcher pick the community
  // up without a reload.
  $effect(() => {
    if (!claimInFlight && !importing) return;
    const timer = setInterval(() => void poll(), POLL_MS);
    return () => clearInterval(timer);
  });

  async function poll(): Promise<void> {
    try {
      const next = await fetchOnboarding();
      if (next.community_held && !onboarding?.community_held) {
        me = asAdminUser(await refreshBootstrap());
      }
      onboarding = next;
    } catch (err: unknown) {
      handle401(err, navigate);
    }
  }

  $effect(() => {
    if (onboarding?.access !== 'needs_invite') return;
    const timer = setInterval(() => void confirm(true), INVITE_CHECK_MS);
    return () => clearInterval(timer);
  });

  async function confirm(auto: boolean): Promise<void> {
    if (confirming) return;
    confirming = true;
    if (!auto) confirmError = null;
    try {
      const next = await confirmAccess();
      // It may have started the claim's audit; let the effect below open that one.
      if (next.audit_id !== onboarding?.audit_id) openedFor = null;
      onboarding = next;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      if (!auto) confirmError = messageOf(err);
    } finally {
      confirming = false;
    }
  }

  async function claim(e: SubmitEvent): Promise<void> {
    e.preventDefault();
    if (claiming || !claimUrl.trim()) return;
    claiming = true;
    claimError = null;
    try {
      onboarding = await claimCommunity(claimUrl.trim());
      adding = false;
      claimUrl = '';
      // An already-imported community is granted on the spot.
      if (onboarding.community_held) me = asAdminUser(await refreshBootstrap());
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      claimError =
        isCodedError(err) && err.code === 'community_already_claimed'
          ? 'That community is already managed by another Sideguide account. If it’s yours, contact us.'
          : messageOf(err);
    } finally {
      claiming = false;
    }
  }

  async function copyEmail(): Promise<void> {
    if (!onboarding) return;
    try {
      await navigator.clipboard.writeText(onboarding.invite_email);
      copied = true;
      setTimeout(() => (copied = false), 2000);
    } catch {
      /* clipboard unavailable — the address is on screen to copy by hand */
    }
  }
</script>

<AdminShell {me}>
  {#if loading}
    <StateBlock state="loading" />
  {:else if error}
    <StateBlock state="error" message={error} />
  {:else}
    <PageContainer>
      <PageHeader section="Insights" title="Discovery" />

      <div class="space-y-6">
        {#if showClaim}
          <section class="rounded-box border hair bg-base-100 p-5">
            <h2 class="font-display text-lg font-bold tracking-tight">
              {holdsCommunity ? 'Add a community' : 'Connect your community'}
            </h2>
            <p class="mt-1 text-sm text-base-content/70">
              Paste its Skool link. We’ll check where it ranks straight away and start importing it.
            </p>
            <form class="mt-4 flex flex-col gap-2 sm:flex-row" onsubmit={claim}>
              <input
                type="text"
                inputmode="url"
                class="input input-bordered w-full font-mono text-sm sm:flex-1"
                bind:value={claimUrl}
                placeholder="skool.com/your-community"
                aria-label="Community link"
              />
              <button type="submit" class="btn btn-primary gap-2" disabled={claiming || !claimUrl.trim()}>
                {#if claiming}<span class="loading loading-spinner loading-sm"></span>{/if}
                {holdsCommunity ? 'Add community' : 'Connect'}
              </button>
            </form>
            {#if claimError}
              <p class="mt-2 text-sm text-error">{claimError}</p>
            {/if}
            {#if adding && holdsCommunity}
              <button type="button" class="link link-hover mt-3 text-xs text-base-content/60" onclick={() => (adding = false)}>
                Cancel
              </button>
            {/if}
          </section>
        {/if}

        {#if onboarding && community && (claimInFlight || importing)}
          <section class="rounded-box border hair bg-base-100 p-5">
            <div class="flex items-center gap-3">
              <CommunityLogo name={community.name} logoUrl={community.logo_url || null} size="h-10 w-10" textClass="text-sm" />
              <div class="min-w-0">
                <div class="eyebrow">Setting up</div>
                <h2 class="redact truncate font-display text-lg font-bold tracking-tight">{community.name}</h2>
              </div>
            </div>

            {#if onboarding.access === 'member' && indexing}
              {@const done = indexing.steps.filter((s) => FINISHED.has(s.status)).length}
              <div class="mt-4">
                <div class="flex items-baseline justify-between gap-3 text-sm">
                  <span class="text-base-content/80">
                    {#if indexing.state === 'running'}
                      Importing — the rest of Sideguide fills in as each part lands.
                    {:else if indexing.state === 'failed'}
                      The import hit a problem. It retries on its own, and we’ve been told.
                    {:else if indexing.ahead > 0}
                      Queued behind one other import — yours starts as soon as it finishes.
                    {:else}
                      Queued — the import starts within a few minutes.
                    {/if}
                  </span>
                  <span class="shrink-0 tabular-nums text-base-content/60">{done}/{indexing.steps.length}</span>
                </div>
                <progress class="progress progress-primary mt-2 h-1.5 w-full" value={done} max={indexing.steps.length}></progress>
                <ul class="mt-3 grid gap-1.5 text-sm sm:grid-cols-2">
                  {#each indexing.steps as step (step.job)}
                    <li class="flex items-center gap-2">
                      {#if FINISHED.has(step.status)}
                        <span class="w-4 text-center text-success">✓</span>
                      {:else if step.status === 'running'}
                        <span class="flex w-4 justify-center"><span class="loading loading-spinner loading-xs text-primary"></span></span>
                      {:else if step.status === 'failed'}
                        <span class="w-4 text-center font-bold text-error">!</span>
                      {:else}
                        <span class="flex w-4 justify-center"><span class="h-1.5 w-1.5 rounded-full bg-base-300"></span></span>
                      {/if}
                      <span class={FINISHED.has(step.status) || step.status === 'running' ? '' : 'text-base-content/55'}>
                        {STEP_LABELS[step.job] ?? step.job}
                      </span>
                    </li>
                  {/each}
                </ul>
              </div>
            {:else if joinForm}
              <p class="mt-4 text-sm text-base-content/80">
                This community asks everyone a few questions before letting them in. We’ve
                drafted answers — change anything you’d rather say differently, then join.
              </p>
              <form class="mt-4 space-y-3" onsubmit={submitJoin}>
                {#each joinForm as question, i (i)}
                  <label class="block">
                    <!-- privacy-ok: the community's own join question, written by its owner -->
                    <span class="mb-1 block text-sm font-medium">{question.question}</span>
                    {#if question.options.length}
                      <select class="select select-bordered w-full text-sm" bind:value={answers[i]}>
                        <option value="" disabled>Choose one</option>
                        {#each question.options as option (option)}
                          <option value={option}>{option}</option>
                        {/each}
                      </select>
                    {:else}
                      <textarea
                        class="textarea textarea-bordered w-full text-sm"
                        rows="2"
                        bind:value={answers[i]}
                        placeholder="Your answer"
                      ></textarea>
                    {/if}
                  </label>
                {/each}
                {#if joinError}
                  <p class="text-sm text-error">{joinError}</p>
                {/if}
                <div class="flex flex-wrap items-center gap-3">
                  <button type="submit" class="btn btn-primary btn-sm gap-2" disabled={joining || !answersReady}>
                    {#if joining}<span class="loading loading-spinner loading-xs"></span>{/if}
                    Join with these answers
                  </button>
                  <span class="text-xs text-base-content/60">
                    Sent to the community exactly as written here.
                  </span>
                </div>
              </form>
              <p class="mt-4 border-t hair pt-3 text-xs text-base-content/60">
                <!-- privacy-ok: this is our own scraper account (domain.accounts.INVITE_EMAIL),
                     not a member. It is the address we ASK the owner to invite, so blurring
                     it in private mode would hide the instruction it exists to give. -->
                Would rather not answer? Invite <span class="font-mono">{onboarding.invite_email}</span>
                from
                <a class="link link-primary" href={membersUrl} target="_blank" rel="noopener noreferrer">
                  your members page
                </a>
                instead, and we’ll pick it up within a few minutes.
              </p>
            {:else if onboarding.access === 'pending_approval'}
              <p class="mt-4 text-sm text-base-content/80">
                We’ve asked to join.
                <a class="link link-primary" href={`${community.url}/-/pending`} target="_blank" rel="noopener noreferrer">
                  Approve the request
                </a>
                in your community — we’ll notice within a few minutes and start importing.
              </p>
            {:else if onboarding.access === 'needs_invite' || onboarding.access === 'needs_attention'}
              <p class="mt-4 text-sm text-base-content/80">
                {onboarding.access === 'needs_invite'
                  ? 'This is a paid community, so we can’t join it on our own.'
                  : 'We couldn’t join this community on our own.'}
                Open
                <a class="link link-primary" href={membersUrl} target="_blank" rel="noopener noreferrer">
                  your members page
                </a>
                on Skool, press <span class="font-semibold">Invite</span>, and invite this email.
              </p>
              <button
                type="button"
                class="mt-3 flex w-full max-w-md items-center justify-between rounded-field border hair bg-base-200/40 px-3 py-2.5 font-mono text-sm hover:border-primary/40"
                onclick={copyEmail}
                title="Copy to clipboard"
              >
                <!-- privacy-ok: INVITE_EMAIL is OUR scraper account's address, not a member's,
                     and this button exists so the owner can copy it -->
                <span>{onboarding.invite_email}</span>
                <span class="text-xs {copied ? 'text-success' : 'text-base-content/60'}">{copied ? 'Copied ✓' : 'Copy'}</span>
              </button>
              {#if onboarding.access === 'needs_invite'}
                <button type="button" class="btn btn-primary btn-sm mt-3 gap-2" onclick={() => confirm(false)} disabled={confirming}>
                  {#if confirming}<span class="loading loading-spinner loading-xs"></span>{/if}
                  I’ve sent the invite
                </button>
              {:else}
                <button type="button" class="btn btn-ghost btn-sm mt-3 gap-2" onclick={() => confirm(false)} disabled={confirming}>
                  {#if confirming}<span class="loading loading-spinner loading-xs"></span>{/if}
                  Try joining again
                </button>
              {/if}
              {#if confirmError}
                <p class="mt-2 text-sm text-error">{confirmError}</p>
              {/if}
            {:else}
              <div class="mt-4 flex items-center gap-2 text-sm text-base-content/80">
                <span class="loading loading-spinner loading-sm text-primary"></span>
                Joining your community on Skool…
              </div>
            {/if}

            {#if onboarding.access !== 'member'}
              <p class="mt-4 border-t hair pt-3 text-xs text-base-content/60">
                Meanwhile we’re checking where you rank on Skool Discovery — it’ll be here once
                we’re in.
              </p>
            {/if}
          </section>
        {/if}

        {#if offerHelper}
          <section class="rounded-box border hair bg-base-100 p-5">
            <div class="eyebrow">Next step</div>
            <h2 class="mt-0.5 font-display text-lg font-bold tracking-tight">Install the Sideguide Helper</h2>
            <p class="mt-1 text-sm text-base-content/70">
              A small Chrome extension that keeps Sideguide current from your own browser and posts the
              replies you queue here. It takes about two minutes.
            </p>
            <HelperInstallSteps />
          </section>
        {/if}

        {#if auditVisible}
          <section>
            <div class="mb-3 flex flex-wrap items-center gap-2">
              <h2 class="font-display text-lg font-bold tracking-tight">
                {improving || rankless ? 'Choose your keywords' : 'Where you rank on Skool'}
              </h2>
              <div class="ml-auto flex flex-wrap gap-2">
                {#if rankless}
                  <!-- Nothing to go back to: this community ranks for nothing. -->
                {:else if improving}
                  <button class="btn btn-ghost btn-sm" onclick={() => (improving = false)}>Back to rankings</button>
                {:else if run.allRowsIn}
                  <button class="btn btn-primary btn-sm" onclick={() => (improving = true)}>Improve my keywords</button>
                {/if}
                <!-- Shown during setup as well as after: a community in another
                     language meets its first audit on this screen, and this was the one
                     place the measurement could not be corrected. Changing it re-runs,
                     by whichever route the claim's state allows. -->
                {#if !run.busy}
                  <LanguageSelect
                    bind:value={language}
                    resolved={run.community?.language ?? null}
                    onchange={relanguage}
                  />
                {/if}
                {#if !claimInFlight && activeCommunity() && !run.busy}
                  <button class="btn btn-ghost btn-sm" onclick={rerun}>Run again</button>
                {/if}
              </div>
            </div>

            {#if importing}
              <p class="mb-3 text-sm text-base-content/70">
                While we import, here’s where you rank today.
              </p>
            {/if}

            {#if auditNote}
              <div class="rounded-box border hair bg-base-100 p-4">
                <p class="text-sm text-base-content/70">{auditNote}</p>
                {#if claimInFlight}
                  <button type="button" class="btn btn-primary btn-sm mt-3 gap-2" onclick={() => confirm(false)} disabled={confirming}>
                    {#if confirming}<span class="loading loading-spinner loading-xs"></span>{/if}
                    Run the audit
                  </button>
                {/if}
              </div>
            {:else if !claimInFlight && !activeCommunity()}
              <p class="rounded-box border hair bg-base-100 p-4 text-sm text-base-content/70">
                Choose a community to see where it ranks.
              </p>
            {:else}
              {#if run.error}
                <div class="alert alert-error mb-3"><span>{run.error}</span></div>
              {/if}
              {#if rankless}
                <p class="mb-3 rounded-box border hair bg-base-100 p-4 text-sm text-base-content/70">
                  This community sets no discovery keywords on Skool, so it ranks for nothing yet.
                  Here are the ones worth claiming — pick eleven and paste them into your Skool
                  settings.
                </p>
                <KeywordImprove {run} url={auditUrl} />
              {:else if improving}
                <KeywordImprove {run} url={auditUrl} />
              {:else}
                <RankingTable {run} />
              {/if}
            {/if}
          </section>
        {/if}
      </div>
    </PageContainer>
  {/if}
</AdminShell>
